import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { 
  Sparkles, ChevronDown, Maximize2, Minimize2, 
  ArrowUp, ArrowDown, RefreshCw, LayoutGrid, AlertTriangle,
  Check, Tag
} from "lucide-react";

/**
 * REMIX PHILOSOPHY:
 * Remix ≠ generate jokes
 * Remix = try this idea a different way
 * 
 * Everything outputs:
 * - Prompts and questions
 * - Constraints
 * - Alternate structures
 * - NEVER overwrites original text
 * - All actions are reversible
 */

// ============ REMIX CATEGORIES ============

const REMIX_CATEGORIES = {
  expand: {
    id: "expand",
    label: "Expand",
    icon: ArrowUp,
    description: "When an idea is thin or underpowered",
    options: [
      {
        id: "heighten",
        title: "Heighten the Premise",
        prompt: "What happens if this goes 10x further than reality?",
        output_type: "question"
      },
      {
        id: "specificity",
        title: "Add Specificity",
        prompt: "Replace vague words with concrete details. What exact thing? Which specific person? What precise moment?",
        output_type: "checklist"
      },
      {
        id: "explore_angles",
        title: "Explore More Angles",
        prompt: "What are 3 other ways to look at this same idea? Different lenses, same truth.",
        output_type: "template"
      }
    ]
  },
  shrink: {
    id: "shrink",
    label: "Shrink",
    icon: ArrowDown,
    description: "When material rambles",
    options: [
      {
        id: "one_line",
        title: "One-Line Version",
        prompt: "What's the cleanest version of this thought? Can you say it in one breath?",
        output_type: "challenge"
      },
      {
        id: "cut_30",
        title: "Cut 30%",
        prompt: "What could be removed without losing meaning? Circle the essential words.",
        output_type: "checklist"
      },
      {
        id: "kill_setup",
        title: "Kill the Setup",
        prompt: "Does the punch survive on its own? What if you started at the funny part?",
        output_type: "question"
      }
    ]
  },
  flip: {
    id: "flip",
    label: "Flip",
    icon: RefreshCw,
    description: "Unlock surprise",
    options: [
      {
        id: "pov_flip",
        title: "POV Flip",
        prompt: "What if this was told from their perspective instead of yours? You → them.",
        output_type: "rewrite_challenge"
      },
      {
        id: "status_flip",
        title: "Status Flip",
        prompt: "What if the expert was clueless? The authority was incompetent? The small was huge?",
        output_type: "rewrite_challenge"
      },
      {
        id: "belief_flip",
        title: "Belief Flip",
        prompt: "What if the opposite was true? Argue against yourself.",
        output_type: "rewrite_challenge"
      }
    ]
  },
  format: {
    id: "format",
    label: "Format",
    icon: LayoutGrid,
    description: "Explore different joke containers",
    options: [
      {
        id: "list",
        title: "List",
        prompt: "Turn this into a ranked or unordered list. What are the items?",
        output_type: "template"
      },
      {
        id: "actout",
        title: "Act-Out Placeholder",
        prompt: "Where would an act-out fit? Mark the spot with [ACT OUT: description].",
        output_type: "template"
      },
      {
        id: "callback",
        title: "Callback Slot",
        prompt: "Where could this reappear later in a set? What would trigger the callback?",
        output_type: "question"
      }
    ]
  },
  angles: {
    id: "angles",
    label: "Angles",
    icon: AlertTriangle,
    description: "Push boundaries and explore edges",
    options: [
      {
        id: "darker",
        title: "Make It Darker",
        questions: ["What changes?", "What breaks?", "What feels dangerous?"],
        output_type: "reflection"
      },
      {
        id: "cleaner",
        title: "Make It Cleaner",
        questions: ["What stays funny without the edge?", "Where's the universal version?"],
        output_type: "reflection"
      },
      {
        id: "personal",
        title: "Make It More Personal",
        questions: ["What's your real stake here?", "What are you hiding?"],
        output_type: "reflection"
      },
      {
        id: "absurd",
        title: "Make It More Absurd",
        questions: ["What's the most ridiculous version?", "Where does logic leave the room?"],
        output_type: "reflection"
      }
    ]
  },
  tags: {
    id: "tags",
    label: "Tags",
    icon: Tag,
    description: "Stack tags and add follow-up hits",
    options: [
      {
        id: "stack_tags",
        title: "Stack Tags",
        prompt: "What could follow immediately after the punch? What's the next hit while they're still laughing?",
        output_type: "template"
      },
      {
        id: "triple_tag",
        title: "Rule of Three",
        prompt: "Can you add two more punches that escalate? Same setup, three hits.",
        output_type: "template"
      },
      {
        id: "callback_tag",
        title: "Callback Tag",
        prompt: "What earlier bit could you reference here for a bonus laugh?",
        output_type: "question"
      },
      {
        id: "act_out_tag",
        title: "Act-Out Tag",
        prompt: "Can you physicalize a reaction right after the punch? What does your face/body do?",
        output_type: "template"
      },
      {
        id: "reverse_tag",
        title: "Reverse Tag",
        prompt: "What if you immediately contradicted or undermined what you just said?",
        output_type: "question"
      }
    ]
  }
};

// ============ REMIX OPTION CARD ============
const RemixOptionCard = ({ option, onSelect, isSelected }) => {
  return (
    <div 
      className={`
        p-3 rounded-sm border transition-all cursor-pointer
        ${isSelected 
          ? 'border-primary bg-primary/10' 
          : 'border-border hover:border-primary/50 bg-card'
        }
      `}
      onClick={() => onSelect(option)}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <h4 className="font-semibold text-sm leading-tight">{option.title}</h4>
        {isSelected && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
      </div>
      
      {option.prompt && (
        <p className="text-xs text-muted-foreground leading-snug break-words">{option.prompt}</p>
      )}
      
      {option.questions && (
        <ul className="space-y-0.5 mt-1">
          {option.questions.map((q, i) => (
            <li key={i} className="text-xs text-muted-foreground flex items-start gap-1 leading-snug">
              <span className="text-primary flex-shrink-0">•</span>
              <span className="break-words">{q}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ============ WORKSPACE AREA ============

// Helper to convert HTML to readable plain text
const htmlToPlainText = (html) => {
  if (!html) return "";
  
  // Create a temporary element to decode HTML entities
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Convert bullet lists to plain text with bullet points
  const lists = temp.querySelectorAll('ul, ol');
  lists.forEach(list => {
    const items = list.querySelectorAll('li');
    const isOrdered = list.tagName === 'OL';
    items.forEach((item, index) => {
      const prefix = isOrdered ? `${index + 1}. ` : '• ';
      item.textContent = prefix + item.textContent;
    });
  });
  
  // Get text content (this decodes &nbsp; and other entities)
  let text = temp.textContent || temp.innerText || "";
  
  // Clean up extra whitespace but preserve intentional line breaks
  text = text.replace(/\s+/g, ' ').trim();
  
  return text;
};

const RemixWorkspace = ({ selectedOption, originalContent, draftContent, onDraftChange }) => {
  // Convert HTML to readable plain text
  const plainTextContent = htmlToPlainText(originalContent);
  
  if (!selectedOption) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <p className="text-center">
          Select a remix option to start.<br />
          <span className="text-xs">Your original text is safe.</span>
        </p>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      {/* Prompt/Challenge Display */}
      <div className="p-4 bg-primary/10 border-b border-primary/30">
        <h4 className="font-semibold text-sm text-primary mb-1">{selectedOption.title}</h4>
        {selectedOption.prompt && (
          <p className="text-sm">{selectedOption.prompt}</p>
        )}
        {selectedOption.questions && (
          <ul className="mt-2 space-y-1">
            {selectedOption.questions.map((q, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="text-primary font-bold">{i + 1}.</span>
                {q}
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Original Text Reference */}
      <div className="p-3 bg-muted/30 border-b border-border">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          Original (read-only)
        </p>
        <ScrollArea className="max-h-40">
          <p className="text-sm font-mono text-muted-foreground whitespace-pre-wrap pr-2">
            {plainTextContent || "No content yet"}
          </p>
        </ScrollArea>
      </div>
      
      {/* Draft Area */}
      <div className="flex-1 p-4 flex flex-col">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Your draft (will be added to section)
        </p>
        <Textarea
          value={draftContent}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder="Try new angles here... this will be appended to your section when you click Done."
          className="flex-1 resize-none font-mono text-sm bg-background/50"
        />
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Draft content will be added to the bottom of your section.
        </p>
      </div>
    </div>
  );
};

// ============ MAIN REMIX MENU COMPONENT ============
export const RemixMenu = ({ 
  isOpen, 
  onClose, 
  originalContent = "",
  targetLabel = "Bit", // Label for what's being remixed
  isSection = false,   // Whether remixing a section vs whole bit
  onApplyDraft // Callback to append draft to the section/bit content
}) => {
  const [activeCategory, setActiveCategory] = useState("expand");
  const [selectedOption, setSelectedOption] = useState(null);
  const [draftContent, setDraftContent] = useState("");
  
  const categories = Object.values(REMIX_CATEGORIES);
  
  const handleSelectOption = (option) => {
    setSelectedOption(option.id === selectedOption?.id ? null : option);
  };
  
  const handleClose = () => {
    // If there's draft content, append it to the section
    if (draftContent.trim() && onApplyDraft) {
      onApplyDraft(draftContent.trim());
    }
    // Reset state
    setSelectedOption(null);
    setDraftContent("");
    onClose();
  };
  
  const handleDiscard = () => {
    // Close without applying draft
    setSelectedOption(null);
    setDraftContent("");
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDiscard()}>
      <DialogContent className="w-[90vw] max-w-[1200px] h-[80vh] max-h-[80vh] bg-card border-border flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-primary" />
            <DialogTitle className="font-display text-xl uppercase tracking-wider">
              Remix {isSection ? 'Section' : 'Bit'}
            </DialogTitle>
            {targetLabel && (
              <span className="text-sm text-muted-foreground">
                — {targetLabel}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {isSection 
              ? "Remix this section. Draft content will be added to your section."
              : "Remix ≠ generate jokes. Remix = try this idea a different way."
            }
          </p>
        </DialogHeader>
        
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Categories & Options */}
          <div className="w-1/2 border-r border-border flex flex-col">
            {/* Category Tabs */}
            <Tabs value={activeCategory} onValueChange={setActiveCategory} className="flex-1 flex flex-col">
              <div className="border-b border-border px-4">
                <TabsList className="h-12 bg-transparent gap-1">
                  {categories.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <TabsTrigger 
                        key={cat.id} 
                        value={cat.id}
                        className="gap-2 px-3 data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                      >
                        <Icon className="w-4 h-4" />
                        <span className="hidden sm:inline">{cat.label}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
              
              {/* Options List */}
              {categories.map((cat) => (
                <TabsContent key={cat.id} value={cat.id} className="flex-1 mt-0">
                  <div className="p-4">
                    <p className="text-sm text-muted-foreground mb-4">{cat.description}</p>
                    <div className="space-y-3">
                      {cat.options.map((option) => (
                        <RemixOptionCard
                          key={option.id}
                          option={option}
                          onSelect={handleSelectOption}
                          isSelected={selectedOption?.id === option.id}
                        />
                      ))}
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
          
          {/* Right: Workspace */}
          <div className="w-1/2 flex flex-col">
            <RemixWorkspace 
              selectedOption={selectedOption}
              originalContent={originalContent}
              draftContent={draftContent}
              onDraftChange={setDraftContent}
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {draftContent.trim() 
              ? "Draft will be added to your section when you click Done."
              : "Write in the draft area to add new content to your section."
            }
          </p>
          <div className="flex items-center gap-2">
            {draftContent.trim() && (
              <Button variant="ghost" size="sm" onClick={handleDiscard}>
                Discard Draft
              </Button>
            )}
            <Button 
              variant={draftContent.trim() ? "default" : "outline"} 
              onClick={handleClose}
            >
              {draftContent.trim() ? "Done & Add Draft" : "Done"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RemixMenu;
